import { getGlobalSettings } from '../../../stores';
import {
  defaultTtsVoice,
  Design,
  DesignChannelType,
  DesignModuleNode,
  LanguageCode,
  NodeTypeName,
  PromptObjectInput,
  TextToSpeechVoiceOrDefault
} from '../../../types';
import { getTextWithParsedVars, VariableParsing } from './get-text-with-parsed-vars';

export type GetPromptTextProps = {
  /** Channel (only provide for omni-channel) to get text for. */
  channel?: DesignChannelType | null;
  /** The node may have been imported from a design with a different omniChannel state */
  isOmniChannel?: boolean;
  /** Language to get text for. */
  languageCode: LanguageCode;
  /** Prompt node to text text from. */
  node: DesignModuleNode;
  /** Used to check for ttsOverrides & omniChannel features on. */
  serialized?: Design;
  /** TTS voice to check for override text or @see defaultTtsVoice to use default text. */
  ttsVoice?: TextToSpeechVoiceOrDefault;
  /**
   * How to handle variables like "{{app.varName}}" in the text.
   *
   * @default Do not replace variables.
   */
  variableParsing?: VariableParsing;
};

export function getPromptText({
  channel,
  isOmniChannel,
  languageCode,
  node,
  serialized,
  ttsVoice,
  variableParsing
}: GetPromptTextProps): string {
  const { language, ...globalSetings } = getGlobalSettings();
  channel ??= globalSetings.channel;
  languageCode ??= language as LanguageCode;
  ttsVoice ??= globalSetings.ttsVoices?.[languageCode] ?? defaultTtsVoice;
  const { nodeTypeName } = node.props;
  if (nodeTypeName !== NodeTypeName.prompt) {
    throw new Error(`Getting prompt text on a "${nodeTypeName}" node is not allowed.`);
  }
  serialized ??= window.apm.serialized;
  const { ttsOverrides } = serialized.rootSettings.features;
  const { config } = node.props;

  if (ttsOverrides && ttsVoice && ttsVoice !== defaultTtsVoice) {
    const text = config?.ttsOverrides?.[languageCode]?.[ttsVoice]?.text;
    if (text) {
      return text;
    }
  }
  isOmniChannel ??= serialized?.omniChannel.isOmniChannel ?? false;
  const textObject = isOmniChannel && channel ? config.channel?.[channel] ?? getDefaultChannelContent() : config.text;
  const text = textObject?.[languageCode] ?? '';
  const channelText = (isOmniChannel && config.channel) ? (
    config.channel?.[channel ?? DesignChannelType.voice] ?? {}
  ) : config.text;

  const localizedText = channelText?.[languageCode];
  if (!localizedText) {
    return '';
  }

  if (variableParsing && config?.inputs) {
    return getTextWithParsedVars(text, config.inputs as PromptObjectInput[], { variableParsing });
  }

  return text;
}

export function getDefaultChannelContent() {
  const languages = window.apm.serialized.languages;
  let defaultChannelLayer: Record<LanguageCode, string> = {};

  languages.forEach((lang) => {
    defaultChannelLayer[lang.language] = '';
  });

  return defaultChannelLayer;
}
